import classNames from "classnames";
import Input from "components/form/input/Input";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import "./builderMediaGalleryPanel.scss";

export type BuilderMediaGalleryItem = {
  id: number;
  source: string;
  filename?: string;
};

type TBuilderMediaGalleryCategory = "media" | "stock-media";

export type BuilderMediaGalleryPanelProps = {
  myMediaItems: BuilderMediaGalleryItem[];
  stockMediaItems: BuilderMediaGalleryItem[];
  selectedMediaId: number | null;
  onSelectMedia: (item: BuilderMediaGalleryItem) => void;
  uploadSlot: ReactNode;
  showAltField?: boolean;
  alt?: string;
  onAltChange?: (alt: string) => void;
  altInputSuffix?: React.ReactElement;
  altHint?: ReactNode;
  loading?: boolean;
};

export function BuilderMediaGalleryPanel(props: BuilderMediaGalleryPanelProps) {
  const {
    myMediaItems,
    stockMediaItems,
    selectedMediaId,
    onSelectMedia,
    uploadSlot,
    showAltField = true,
    alt = "",
    onAltChange,
    altInputSuffix,
    altHint,
    loading = false,
  } = props;
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] =
    useState<TBuilderMediaGalleryCategory>("media");

  const galleryItems =
    selectedCategory === "media" ? myMediaItems : stockMediaItems;
  const selectedCategoryTitle =
    selectedCategory === "media" ? t("My media") : t("Stock images");

  return (
    <div className="builder-media-gallery-panel">
      <div className="builder-media-gallery-content-container">
        <div className="builder-media-gallery-content">
          <div className="builder-media-gallery-sidebar">
            {uploadSlot}

            <div className="builder-media-gallery-categories">
              <button
                type="button"
                className={selectedCategory === "media" ? "active" : undefined}
                onClick={() => setSelectedCategory("media")}
              >
                <div>{t("My images")}</div>
              </button>
              <button
                type="button"
                className={
                  selectedCategory === "stock-media" ? "active" : undefined
                }
                onClick={() => setSelectedCategory("stock-media")}
              >
                <div>{t("Stock images")}</div>
              </button>
            </div>
          </div>

          <div className="builder-media-gallery-library">
            <div className="builder-media-gallery-library-title">
              {selectedCategoryTitle}
            </div>
            {loading ? (
              <div
                className="builder-media-gallery-library-empty-message"
                aria-live="polite"
              >
                {t("Loading…")}
              </div>
            ) : galleryItems.length === 0 ? (
              <div
                className="builder-media-gallery-library-empty-message"
                aria-live="polite"
              >
                {t("No images available")}
              </div>
            ) : (
              <div className="builder-media-gallery-library-options">
                {galleryItems.map(mediaItem => (
                  <div
                    key={mediaItem.id}
                    className={classNames("builder-media-gallery-option", {
                      "option-current": mediaItem.id === selectedMediaId,
                    })}
                    onClick={() => onSelectMedia(mediaItem)}
                  >
                    <img src={mediaItem.source} draggable={false} alt="" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAltField ? (
        <div className="builder-media-gallery-alt-text">
          <Input
            label={t("Image alt text (optional)")}
            suffix={altInputSuffix}
            value={alt}
            onChange={event => {
              onAltChange?.(event.target.value);
            }}
            placeholder={t("E.g., Golf course with green trees")}
          />
          {altHint}
        </div>
      ) : null}
    </div>
  );
}
